import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { collection, addDoc, getDocs, query, where, orderBy, serverTimestamp, doc, getDoc, setDoc, onSnapshot, deleteDoc } from 'firebase/firestore';
import { firestore } from './firebaseConfig';
import ChatRoomList from './components/ChatRoomList';
import UserProfile from './components/UserProfile';
import './ChatRoomListPage.scss';
import * as S from '../scss/theme';
import fnc from '../mymod/commFunction';
import { getCanonicalTalkUserId } from './talkUserId';



const ChatRoomListPage = () => {
    const navigate = useNavigate();

    const userInfo = useSelector((state) => state.reduxStore.userInfo);
    const userInfo2 = useSelector((state) => state.reduxStore.userInfo2);
    const [chatrooms, setChatrooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userProfile, setUserProfile] = useState(null);
    
    // Firebase Anonymous Auth 대신 사용자 ID 직접 사용
    const academyCode = userInfo2?.acaCode || fnc.getCookie('acaCode') || '';
    const userId = getCanonicalTalkUserId(userInfo2, userInfo, academyCode);

    /**
     * 채팅방 목록 실시간 리스너 설정
     * 현재 사용자가 참여한 채팅방만 로드하여 실시간으로 업데이트
     */
    useEffect(() => {
        if (!userId) return;

        const unsubscribe = loadChatrooms(userId);
        
        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [userId]);

    /**
     * 사용자 프로필 초기화
     * Firebase Auth 대신 Redux 상태의 사용자 정보 사용
     */
    useEffect(() => {
        if (!userId || !userInfo2) return;

        const initializeUserProfile = async () => {
            try {
                // Firestore에서 사용자 정보 확인
                const userDocRef = doc(firestore, 'users', userId);
                const userDocSnap = await getDoc(userDocRef);
                
                if (!userDocSnap.exists()) {
                    // 새 사용자 - Firestore에 저장
                    await setDoc(userDocRef, {
                        id: userId,
                        nickname: userInfo2?.nickname || '',
                        id2: userInfo2?.id2 || '',
                        profile_image: userInfo2?.profile_image || '',
                        email: userInfo2?.email || userInfo?.email || '',
                        createdAt: serverTimestamp(),
                        lastSeen: serverTimestamp(),
                        isOnline: true
                    });
                }
                
                setUserProfile({
                    id: userId,
                    realId: userId,
                    id2: userInfo2?.id2 || '',
                    nickname: userInfo2?.nickname || '',
                    email: userInfo2?.email || userInfo?.email || '',
                    phoneNumber: userInfo2?.hp || '',
                    profile_image: userInfo2?.profile_image || ''
                });
                
                setLoading(false);
            } catch (error) {
                console.error('프로필 초기화 오류:', error);
                setLoading(false);
            }
        };

        initializeUserProfile();
    }, [userId, userInfo2]);

    /**
     * 사용자가 참여한 채팅방 목록을 실시간으로 로드
     * @param {string} userId - 사용자 ID
     * @returns {Function} unsubscribe 함수
     */
    const loadChatrooms = (userId) => {
        try {
            const q = query(
                collection(firestore, 'chatRooms'),
                where('participants', 'array-contains', userId)
            );
            
            const unsubscribe = onSnapshot(q, (querySnapshot) => {
                const chatroomsList = [];
                querySnapshot.forEach((doc) => {
                    chatroomsList.push({
                        id: doc.id,
                        ...doc.data()
                    });
                });
                
                // 마지막 메시지 시간 기준 내림차순 정렬
                chatroomsList.sort((a, b) => {
                    const timeA = a.lastMessageTime?.toDate?.() || new Date(0);
                    const timeB = b.lastMessageTime?.toDate?.() || new Date(0);
                    return timeB - timeA;
                });
                
                setChatrooms(chatroomsList);
            }, (error) => {
                console.error('채팅방 로드 오류:', error);
                setChatrooms([]);
            });
            
            return unsubscribe;
        } catch (error) {
            console.error('채팅방 리스너 설정 오류:', error);
        }
    };

    /**
     * 새 채팅방 생성
     * @param {string} roomName - 채팅방 이름
     * @param {Array} selectedUserIds - 선택된 사용자 ID 배열
     */
    const createChatroom = async (roomName, selectedUserIds) => {
        if (!userId) return;

        try {
            // 참여자 목록: 생성자 + 선택된 사용자들 (중복 제거)
            const allParticipants = [...new Set([userId, ...selectedUserIds])];
            
            // 모든 참여자의 읽지 않은 메시지 수 초기화
            const unreadCount = {};
            allParticipants.forEach(participantId => {
                unreadCount[participantId] = 0;
            });

            const chatroomData = {
                name: roomName,
                participants: allParticipants,
                createdBy: userId,
                createdAt: serverTimestamp(),
                lastMessage: `${userInfo2?.nickname || '사용자'}님이 채팅방을 만들었습니다.`,
                lastMessageTime: serverTimestamp(),
                unreadCount: unreadCount,
                isActive: true
            };

            const docRef = await addDoc(collection(firestore, 'chatRooms'), chatroomData);
            
            // 새로 생성한 채팅방으로 이동
            navigate(`/chatWindowPage/${docRef.id}`);
        } catch (error) {
            console.error('채팅방 생성 오류:', error);
            alert('채팅방 생성에 실패했습니다: ' + error.message);
        }
    };

    // 채팅방 선택
    const selectChatroom = (chatroom) => {
        navigate(`/chatWindowPage/${chatroom.id}`);
    };

    // 채팅방 삭제
    const deleteChatroom = async (chatroomId) => {
        try {
            await deleteDoc(doc(firestore, 'chatRooms', chatroomId));
        } catch (error) {
            console.error('채팅방 삭제 오류:', error);
            alert('채팅방 삭제에 실패했습니다: ' + error.message);
        }
    };

    if (loading) {
        return (
            <div className="chat-list-loading">
                <div className="loading-spinner"></div>
                <p>로딩 중...</p>
            </div>
        );
    }

    return (
        <S.Section> 
        <div className="ChatRoomListPage">
            <UserProfile 
                user={userProfile} 
                onUpdateProfile={setUserProfile}
                currentUser={userProfile}
            />
            <ChatRoomList
                chatrooms={chatrooms}
                currentChatroom={null}
                onSelectChatroom={selectChatroom}
                onCreateChatroom={createChatroom}
                onDeleteChatroom={deleteChatroom}
                currentUserId={userId}
            />
        </div>
        </S.Section>
    );
};

export default ChatRoomListPage;
